import { Sprite, Texture, Point } from 'pixi.js';
import MathUtils from '../../../utils/MathUtils';
import type { EasingFunction, EasingMap } from '../../../libs/TweenManager/easing';
import type { Tween } from '../../../libs/TweenManager/Tween';

export class ResourceAnimationSprite extends Sprite {
  private readonly from = new Point();

  private readonly to = new Point();

  private readonly controlPoint = new Point();

  private animationTime = 0;

  private tween?: Tween;

  public init(
    spriteFrame: string | undefined,
    from: Point,
    to: Point,
    duration: number,
    delay: number,
    ease: EasingFunction | keyof typeof EasingMap,
    scale: number,
    onComplete: () => void,
  ): this {
    if (spriteFrame) this.texture = Texture.from(spriteFrame);

    from.copyTo(this.from);
    to.copyTo(this.to);

    this.props({
      scaleX: scale,
      scaleY: scale,
      x: from.x,
      y: from.y,
      alpha: 0,
      anchor: 0.5,
    });

    this.animationTime = 0;

    this.updateControlPoint();

    const tween = this.tweenTo({ animationTime: 1 }, duration, ease, true, delay);
    tween.onUpdate.add((): void => {
      this.updateMovement(this.animationTime);
    });
    tween.onComplete.add((): void => {
      this.updateMovement(this.animationTime);
      this.parent?.removeChild(this);
      onComplete();
    });

    this.tween = tween;

    return this;
  }

  private updateControlPoint(): void {
    const { to, from, controlPoint } = this;
    controlPoint.set(from.x + (to.x - from.x) / 2, from.y + (to.y - from.y) / 2);
    const distance = MathUtils.getDistance(from.x, from.y, to.x, to.y);
    const controlPointOffsetX = distance * MathUtils.getRandomValueInRange(0.33, 0.37);
    controlPoint.x -= Math.sign(from.x - to.x) * controlPointOffsetX;
  }

  private updateMovement(time: number): void {
    const { from, to, controlPoint } = this;
    const curveXY = MathUtils.getBezierCurvePosition(from, controlPoint, to, time);
    this.x = curveXY.x;
    this.y = curveXY.y;

    if (time < 0.05) this.alpha = time / 0.05;
    else if (time > 0.95) this.alpha = (1 - time) / 0.05;
    else this.alpha = 1;
  }

  public stopAnimation(): void {
    this.tween?.stop(true);
  }
}
